import React, { useEffect, useState } from 'react';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Editar los datos de la persona desde su ficha. El nombre que se cambia acá
 * llega a todas sus entrevistas, retests e informes.
 */
export default function EditarAlumnoDialog({ alumno, open, onOpenChange, onGuardado }) {
  const [form, setForm] = useState({});
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        nombre: alumno.nombre || '',
        sexo: alumno.sexo || '',
        fecha_nacimiento: alumno.fecha_nacimiento || '',
        telefono: alumno.telefono || '',
        email: alumno.email || '',
      });
    }
  }, [open, alumno]);

  const guardar = async () => {
    const nombre = (form.nombre || '').trim();
    if (!nombre) {
      toast.error('Ingresá el nombre del alumno');
      return;
    }
    const payload = { ...form, nombre };
    ['sexo', 'fecha_nacimiento', 'telefono', 'email'].forEach((k) => {
      if (!payload[k]) payload[k] = null;
    });
    setGuardando(true);
    try {
      const actualizado = await api.updateAlumno(alumno.id, payload);
      toast.success(nombre !== alumno.nombre
        ? 'Datos guardados. El nombre nuevo ya figura en todas sus entrevistas.'
        : 'Datos guardados');
      onGuardado(actualizado);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.message || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !guardando && onOpenChange(o)}>
      <DialogContent className="max-w-lg" data-testid="editar-alumno-dialog">
        <DialogHeader>
          <DialogTitle>Editar datos del alumno</DialogTitle>
          <DialogDescription>
            El nombre que cambies acá se actualiza en todas sus entrevistas, retests e informes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="ed-nombre">Nombre y apellido *</Label>
            <Input id="ed-nombre" value={form.nombre || ''} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ed-sexo">Sexo</Label>
              <select
                id="ed-sexo"
                value={form.sexo || ''}
                onChange={(e) => setForm({ ...form, sexo: e.target.value })}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Sin especificar</option>
                <option value="Masculino">Masculino</option>
                <option value="Femenino">Femenino</option>
                <option value="No binario">No binario</option>
                <option value="Prefiero no responder">Prefiero no responder</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ed-nac">Fecha de nacimiento</Label>
              <Input id="ed-nac" type="date" value={form.fecha_nacimiento || ''} onChange={(e) => setForm({ ...form, fecha_nacimiento: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ed-tel">Teléfono</Label>
            <Input id="ed-tel" value={form.telefono || ''} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="Opcional" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ed-email">Email</Label>
            <Input id="ed-email" type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Opcional" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={guardando}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando} data-testid="editar-alumno-guardar">
            {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
